import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { useEffect, useState, useRef, useContext, useCallback } from 'preact/hooks';

import { emptyCountry, AppContext } from '../context/AppContext';
import { sanitizeHtml } from '../helpers/sanitizeHtml';
import { CloseIcon, TccEagleIcon } from './icons/icons';

const fetchPost = async ( { post_id }: { post_id: number } ) => {
	if ( ! post_id ) {
		return null;
	}

	// Create session storage key for this post.
	const sessionStorageKey = 'tcc_country_' + post_id;

	// Check if post is already stored, return if found.
	const sessionStoragePost = sessionStorage.getItem( sessionStorageKey );
	if ( sessionStoragePost ) {
		return JSON.parse( sessionStoragePost );
	}

	// If not in stored, fetch from API.
	const requestUrl = '/wp-json/we/v2/country/' + post_id;
	const response = await apiFetch( { path: requestUrl } );

	// Save to session storage.
	sessionStorage.setItem( sessionStorageKey, JSON.stringify( response ) );

	return response;
};

function FeaturedPost( { featured }: {
	featured: {
		title: string,
		date: string,
		link: string,
		image: string,
		terms: string[]
	}} ) {
	// Check if empty.
	if ( ! featured || Object.keys( featured ).length === 0 || ! featured.title ) {
		return null;
	}

	const { title, date, link, image, terms } = featured;

	return (
		<div className="tcc-map__dialog-featured-post">
			<h3 className="h5">{ __( 'Featured Post', 'tcc-map' ) }</h3>

			<div className="tcc-map__dialog-featured-post-item">
				{ image ? (
					<div className="tcc-map__dialog-featured-image" dangerouslySetInnerHTML={ { __html: sanitizeHtml( image ) } } />
				) : (
					<div className="tcc-map__dialog-featured-image render-image-wrapper render-image--fallback" style={ { '--render-image-aspect-ratio': '16/9' } }>
						<TccEagleIcon />
					</div>
				)}
				
				<div className="tcc-map__dialog-featured-post-content">
					{ title && (
						<h4 className="link-level-1 tcc-map__dialog-featured-post-title">
							<a href={ link } className="post-title-link" dangerouslySetInnerHTML={ { __html: sanitizeHtml( title ) } } />
						</h4>
					) }

					{ ( terms.length > 0 || date ) ? (
						<ul className="tcc-map__dialog-post-meta helio-post-meta">
							{ terms.length > 0 && (
								<li className="terms">
									{ terms.map( ( term, index ) => (
										<>
											<span className="term" key={ `${term}-${index}` } dangerouslySetInnerHTML={ { __html: sanitizeHtml( term ) } } />
											{ index < terms.length - 1 ? ', ' : '' }
										</>
									) ) }
								</li>
							) }
							{ date && (
								<li className="date">{ date }</li>
							) }
						</ul>
					) : null}
				</div>
			</div>
		</div>
	);
}

function CountryPost( { post }: {
	post: {
		title: string,
		programs: string[],
		intro: string,
		link: string,
		goal: string,
		impacts: string[],
		featured: {
			title: string,
			date: string,
			link: string,
			image: string,
			terms: string[]
		}
	}
	} ) {
	if ( ! post ) {
		return null;
	}

	const { title, programs, intro, link, goal, impacts, featured } = post;

	return (
		<div className="tcc-map__dialog-post">
			<h2 className="tcc-map__dialog-title" dangerouslySetInnerHTML={ { __html: sanitizeHtml( title ) } } />
			{ programs.length > 0 && (
				<ul className="tcc-map__dialog-programs">
					{ programs.map( ( program, index ) => (
						<li key={ index } className="tag-label-unclickable" dangerouslySetInnerHTML={ { __html: sanitizeHtml( program ) } } />
					) ) }
				</ul>
			) }
			{ intro && (
				<p className="tcc-map__dialog-intro caption-text" dangerouslySetInnerHTML={ { __html: sanitizeHtml( intro ) } } />
			) }
			<div className="tcc-map__dialog-details">
				{ goal && (
					<div className="tcc-map__dialog-goal-wrapper">
						<h3 className="h5">{ __( 'Short-term Goal', 'tcc-map' ) }</h3>
						<p className="tcc-map__dialog-goal caption-text" dangerouslySetInnerHTML={ { __html: sanitizeHtml( goal ) } } />
					</div>
				) }
				{ impacts.length > 0 && (
					<div className="tcc-map__dialog-impacts">
						<h3 className="h5">{ __( 'Impact', 'tcc-map' ) }</h3>
						<ul className="tcc-map__dialog-impacts caption-text">
							{ impacts.map( ( impact, index ) => (
								<li key={ index } dangerouslySetInnerHTML={ { __html: sanitizeHtml( impact ) } } />
							) ) }
						</ul>
					</div>
				) }
				<FeaturedPost featured={ featured } />
			</div>
			<a href={ link } className="tcc-map__dialog-button btn">{ __( 'View Country', 'tcc-map' ) }</a>
		</div>
	);
}

export default function CountryDialog() {
	const { filterContext } = useContext( AppContext );
	const [ filterState, dispatch ] = filterContext;
	const { activeCountry } = filterState;

	const dialogRef = useRef<HTMLDialogElement>( null );
	const closeDialogRef = useRef<HTMLButtonElement>( null );

	const [ isOpen, setIsOpen ] = useState( false );
	const [ countryPost, setCountryPost ] = useState( null );
	const [ loading, setLoading ] = useState( false );

	// Handle dialog open / close.
	const handleToggle = useCallback( ( forcedState: boolean|null ) => {
		const newIsOpen = forcedState ?? ! isOpen;

		// Do nothing if state is the same.
		if ( newIsOpen === isOpen ) {
			return;
		}

		if ( newIsOpen ) {
			dialogRef.current.show();
		} else {
			dialogRef.current.close();
			dispatch( { type: 'SET_ACTIVE_COUNTRY', payload: emptyCountry } );
		}

		setIsOpen( newIsOpen );
	}, [ isOpen, dispatch ] );

	// Fetch post data.
	useEffect( () => {
		( async () => {
			setLoading( true );

			try {
				const post = await fetchPost( { post_id: activeCountry.post_id } );
				setCountryPost( post );
				setLoading( false );
			} catch ( error ) {
				// eslint-disable-next-line no-console
				console.warn( 'Error fetching country post:', error );
			}
		} )();
	}, [ activeCountry ] );

	// Open / close dialog when activeCountry changes.
	useEffect( () => {
		if ( activeCountry.post_id ) {
			handleToggle( true );
		} else {
			handleToggle( false );
		}
	}, [ activeCountry, handleToggle ] );

	// Focus close button when dialog opens.
	useEffect( () => {
		if ( isOpen && closeDialogRef.current ) {
			setTimeout( () => {
				closeDialogRef.current.focus();
			}, 100 );
		}
	}, [ isOpen ] );

	return (

		<dialog ref={ dialogRef } className={ `tcc-map__dialog section-background--navy-100 ${ isOpen ? 'tcc-map__dialog--open' : '' }` }>
			<div className="tcc-map__dialog-inner">

				<button
					className="tcc-map__dialog-close"
					onClick={ () => handleToggle( false ) }
					ref={ closeDialogRef }
				>
					<span className="screen-reader-text">{ __( 'Close Dialog', 'tcc-map' ) }</span>
					<CloseIcon />
				</button>

				<div className="tcc-map__dialog-content">

					{ loading && (
						<p>{ __( 'Loading…', 'tcc-map' ) }</p>
					) }

					{ isOpen && (
						<CountryPost post={ countryPost } />
					) }
				</div>

			</div>
		</dialog>

	);
}